import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { GrAdd, GrClose } from "react-icons/gr";
import { FaPlus } from 'react-icons/fa';
import ReactLoading from 'react-loading';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Helmet  from 'react-helmet';
const Links = () => {

  const API_BASE_URL = 
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_URL_PRODUCTION
      : process.env.REACT_APP_API_URL;

  // Single Link item component
  const LinkDetails = ({ mylink, deleteHandler, updateHandler }) => {
    const [name, setName] = useState(mylink.name);
    const [link, setLink] = useState(mylink.link);
    const [description, setDescription] = useState(mylink.description);
    const [tags, setTags] = useState(mylink.tags);
    const [error, setError] = useState(null);
    const [emptyFields, setEmptyFields] = useState([]);

    const [isUpdating, setIsUpdating] = useState(false);
    const [thisLink, setThisLink] = useState("");

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    useEffect(() => {
      setThisLink(
        mylink.link.toLowerCase().startsWith("https://")
          ? mylink.link
          : "https://".concat(mylink.link)
      );
    }, [mylink.link]);

    useEffect(() => {
      const handleEsc = (event) => {
        if (event.key === "Escape") {
          setOpenUpdate(false);
          setOpenDelete(false);
        }
      };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const toggleUpdate = () => setOpenUpdate(prev => !prev);
    const toggleDelete = () => setOpenDelete(prev => !prev);

    const handleUpdate = async (e) => {
      e.preventDefault();
      setIsUpdating(true);

      if (!name || !link) {
        setError("Please input all required fields");
        setIsUpdating(false);
        return;
      }

      const newlink = { name, link, description, tags };
      const response = await fetch(`${API_BASE_URL}/api/links/${mylink._id}`, {
        method: 'PATCH',
        body: JSON.stringify(newlink),
        headers: { 'Content-Type': 'application/json' }
      });

      const json = await response.json();

      if (!response.ok) {
        setError(json.error);
        setEmptyFields(json.emptyFields);
        setIsUpdating(false);
      } else {
        setName('');
        setLink('');
        setDescription('');
        setTags('');
        setError(null);
        setEmptyFields([]);
        updateHandler();
        setIsUpdating(false);
        toggleUpdate();
      }
    };

    const handleDelete = async () => {
      const response = await fetch(`${API_BASE_URL}/api/links/${mylink._id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        deleteHandler();
        toggleDelete();
      }
    };

    return (
      <div className="link-cap" key={mylink._id}>
        <p className='none'>{emptyFields}</p>

        <div className="linkRow">
          <Link to={thisLink} target="_blank" className="linkNameDiv" rel="noopener noreferrer">
            {mylink.name.length > 60
              ? `${mylink.name.slice(0, 30)}...`
              : mylink.name}
          </Link>

          <div className="linkButtonDiv">
            <div className="linkButton" onClick={toggleUpdate} aria-label="Edit link">
              <EditIcon />
            </div>
            <div className="linkButton" onClick={toggleDelete} aria-label="Delete link">
              <DeleteIcon />
            </div>
          </div>
        </div>

        {openUpdate && (
          <div className="modalOverlay">
            <div className="modalContent">
              <h4 className='modalTitle'>Update Link</h4>
              <form className="modalForm" onSubmit={handleUpdate}>
                <div className="modalInputDiv">
                  <label className="modalInputLabel">Name:</label>
                  <input
                    className="modalInput"
                    type="text"
                    onChange={e => setName(e.target.value)}
                    value={name}
                    required
                  />
                </div>

                <div className="modalInputDiv">
                  <label className="modalInputLabel">Link:</label>
                  <input
                    className="modalInput"
                    type="text"
                    onChange={e => setLink(e.target.value)}
                    value={link}
                    required
                  />
                </div>

                <div className="modalInputDiv">
                  <label className="modalInputLabel">Description:</label>
                  <textarea
                    className="modalTextArea"
                    onChange={e => setDescription(e.target.value)}
                    value={description}
                  />
                </div>

                <div className="modalInputDiv">
                  <label className="modalInputLabel">Tags:</label>
                  <input
                    className="modalInput"
                    type="text"
                    onChange={e => setTags(e.target.value)}
                    value={tags}
                  />
                </div>

                {error && <div className="modalError">{error}</div>}

                {isUpdating ? (
                  <div className='loading-div'>
                    <ReactLoading type='bubbles' />
                  </div>
                ) : (
                  <div className="modalButtonDiv">
                    <button type="submit" className="modalButton modalSubmitButton">Update</button>
                    <button type="button" className="modalButton modalCancelButton" onClick={toggleUpdate}>Cancel</button>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {openDelete && (
          <div className="modalOverlay">
            <div className="modalAlertDiv">
              <div className="modalTitle">Are you sure you want to delete?</div>
              <div className="modalText">
                {mylink.name.length > 30
                  ? `${mylink.name.slice(0, 30)}...`
                  : mylink.name}
              </div>
              <div className="modalButtonDiv">
                <button className="modalButton modalSubmitButton" onClick={handleDelete}>Delete</button>
                <button className="modalButton modalCancelButton" onClick={toggleDelete}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Main Links state and handlers
  const [allLinks, setAllLinks] = useState([]);
  const [openAddLink, setOpenAddLink] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const linksPerPage = 10;

  const totalPages = Math.ceil(
    allLinks.filter(link =>
      link.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.link.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.description.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.tags.toLowerCase().includes(searchValue.toLowerCase())
    ).length / linksPerPage
  );

  const searchHandler = useCallback((e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  }, []);

  // Flags to trigger reload on add, update, delete
  const [addLinkFlag, setAddLinkFlag] = useState(false);
  const [updateLinkFlag, setUpdateLinkFlag] = useState(false);
  const [deleteLinkFlag, setDeleteLinkFlag] = useState(false);

  const addHandler = () => setAddLinkFlag(true);
  const updateHandler = () => setUpdateLinkFlag(true);
  const deleteHandler = () => setDeleteLinkFlag(true);

  useEffect(() => {
    setIsLoading(true);
    setOpenAddLink(false);

    const fetchLinks = async () => {
      const response = await fetch(`${API_BASE_URL}/api/links`);
      const json = await response.json();

      if (response.ok) {
        const sortedLinks = json.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllLinks(sortedLinks);
        setAddLinkFlag(false);
        setUpdateLinkFlag(false);
        setDeleteLinkFlag(false);
        setIsLoading(false);
      }
    };

    fetchLinks();
  }, [addLinkFlag, updateLinkFlag, deleteLinkFlag, API_BASE_URL]);

  const addLinkBtnHandler = () => setOpenAddLink(prev => !prev);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'a') {
        setOpenAddLink(prev => !prev);
      }
      if (e.key === 'Escape') {
        setOpenAddLink(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    const newlink = { name, link, description, tags };

    const response = await fetch(`${API_BASE_URL}/api/links`, {
      method: 'POST',
      body: JSON.stringify(newlink),
      headers: { 'Content-Type': 'application/json' }
    });

    const json = await response.json();

    if (!response.ok) {
      setError(json.error);
      setIsAdding(false);
    } else {
      setName('');
      setLink('');
      setDescription('');
      setTags('');
      setError(null);
      addHandler();
      setIsAdding(false);
    }
  };

  const displayedLinks = allLinks
    .filter(link =>
      link.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.link.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.description.toLowerCase().includes(searchValue.toLowerCase()) ||
      link.tags.toLowerCase().includes(searchValue.toLowerCase())
    )
    .slice((currentPage - 1) * linksPerPage, currentPage * linksPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <div className="links-wrapper">
      <Helmet>
        <title>NavarroJalen | Links</title>
        <meta name="description" content="NavarroJalen | Links" />
        <meta property="og:title" content="NavarroJalen | Links" />
      </Helmet>
      <div className="subMenuBar">
      
         
          {openAddLink 
            ? <GrClose onClick={addLinkBtnHandler} className="subMenuBarButton" /> 
            : <FaPlus onClick={addLinkBtnHandler}  className="subMenuBarButton" />
          }
   
          <input
            type="text"
            placeholder="Search Link..."
            className="searchbar"
            value={searchValue}
            onChange={searchHandler}
          />
   

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={currentPage === 1}>Prev</button>
            <span className="paginationInfo">Page {currentPage} of {totalPages}</span>
            <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
          </div>
        )}
      </div>

      {openAddLink && (
        <div className="modalOverlay">
          <div className="modalContent">
            <form className="modalForm" onSubmit={handleSubmit}>
              <h2 className="modalTitle">Add Link</h2>

              <div className="modalInputDiv">
                <label className="modalInputLabel">Name: <span className="label-require">*</span></label>
                <input
                  className="modalInput"
                  type="text"
                  onChange={e => setName(e.target.value)}
                  value={name}
                  required
                />
              </div>

              <div className="modalInputDiv">
                <label className="modalInputLabel">Link: <span className="label-require">*</span></label>
                <input
                  className="modalInput"
                  type="text"
                  onChange={e => setLink(e.target.value)}
                  value={link}
                  required
                />
              </div>

              <div className="modalInputDiv">
                <label className="modalInputLabel">Description:</label>
                <textarea
                  className="modalTextArea"
                  onChange={e => setDescription(e.target.value)}
                  value={description}
                />
              </div>

              <div className="modalInputDiv">
                <label className="modalInputLabel">Tags:</label>
                <input
                  className="modalInput"
                  type="text"
                  onChange={e => setTags(e.target.value)}
                  value={tags}
                />
              </div>

              {error && <div className="modalError">{error}</div>}

              {isAdding ? (
                <div className="loading-div"><ReactLoading type='bubbles' /></div>
              ) : (
                <div className="modalButtonDiv">
                  <button type="submit" className="modalButton modalSubmitButton">Add Link</button>
                  <button
                    type="button"
                    className="modalButton modalCancelButton"
                    onClick={() => setOpenAddLink(false)}
                  >Cancel</button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      <div className="linksTable">
        {isLoading ? (
          <div className="loading-div">
            <ReactLoading type='bubbles' />
          </div>
        ) : (
          displayedLinks.length === 0 ? (
            <p className="links-empty-text">No links found</p>
          ) : (
            <div className="link-maindiv">
              {displayedLinks.map(link => (
                <LinkDetails
                  key={link._id}
                  mylink={link}
                  deleteHandler={deleteHandler}
                  updateHandler={updateHandler}
                />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Links;
